/**
 * Sources for an assistant turn, shared by web and native.
 *
 * A "source" here is an external page the agent's tools actually touched — a
 * job posting on a company site, a catalog listing, a connected service. They
 * are read straight out of tool results, never fabricated, and only shown when
 * there is at least one. A turn that read the user's own profile has no
 * sources, and says nothing.
 */
import type { TracePart } from './activity-trace-model';
import { isVisibleToolPart } from './activity-trace-model';

export type StreamingSource = {
  name: string;
  domain: string;
  href: string;
};

const URL_KEYS = ['url', 'applyUrl', 'sourceUrl', 'externalUrl', 'link', 'href', 'website', 'jobUrl'];
const NAME_KEYS = ['title', 'name', 'companyName', 'company', 'label', 'source'];

const MAX_SOURCES = 8;
const MAX_DEPTH = 4;

/**
 * Research `readUrl` outcomes that did not read: they sit next to the real
 * sources in the result (`urls[]`), but a page that could not be opened is
 * not a source.
 */
const UNREAD_STATUSES = new Set(['error', 'paywall', 'unsafe']);

function normalizeDomain(value: string): string | null {
  const domain = value.trim().toLowerCase().replace(/^www\./, '');
  return domain && !/\s/.test(domain) ? domain : null;
}

function domainOf(href: string): string | null {
  try {
    return normalizeDomain(new URL(href).hostname);
  } catch {
    return null;
  }
}

/**
 * Research sources carry the publisher's `domain`. For web search the `url` is
 * a grounding redirect (vertexaisearch.cloud.google.com/...), so labelling by
 * the URL's host would show the redirector for every source. The link itself
 * is still opened as is.
 */
function readDomain(record: Record<string, unknown>, href: string): string | null {
  const declared = record.domain;
  if (typeof declared === 'string') {
    const domain = normalizeDomain(declared);
    if (domain) return domain;
  }
  return domainOf(href);
}

function isUnreadOutcome(record: Record<string, unknown>): boolean {
  return (
    typeof record.status === 'string' &&
    UNREAD_STATUSES.has(record.status) &&
    typeof record.message === 'string'
  );
}

function readName(record: Record<string, unknown>): string | undefined {
  for (const key of NAME_KEYS) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = (value as Record<string, unknown>).name;
      if (typeof nested === 'string' && nested.trim()) return nested.trim();
    }
  }
  return undefined;
}

function collect(value: unknown, depth: number, out: Map<string, StreamingSource>) {
  if (out.size >= MAX_SOURCES || depth > MAX_DEPTH || !value || typeof value !== 'object') return;

  if (Array.isArray(value)) {
    for (const item of value) collect(item, depth + 1, out);
    return;
  }

  const record = value as Record<string, unknown>;
  if (isUnreadOutcome(record)) return;
  for (const key of URL_KEYS) {
    const href = record[key];
    if (typeof href !== 'string' || !/^https?:\/\//i.test(href)) continue;
    const domain = readDomain(record, href);
    if (!domain || out.has(href)) continue;
    out.set(href, { href, domain, name: readName(record) ?? domain });
    break;
  }

  for (const child of Object.values(record)) {
    if (child && typeof child === 'object') collect(child, depth + 1, out);
  }
}

export function deriveSources(
  parts: readonly TracePart[],
  hiddenTools: ReadonlySet<string>,
): StreamingSource[] {
  const out = new Map<string, StreamingSource>();
  for (const part of parts) {
    if (!isVisibleToolPart(part, hiddenTools)) continue;
    if (part.stage !== 'completed' || part.isError) continue;
    collect(part.result, 0, out);
    if (out.size >= MAX_SOURCES) break;
  }
  return [...out.values()];
}

function comparableHost(value: string): string {
  return value.trim().toLowerCase().replace(/^www\./, '');
}

/**
 * Whether a source row should print the domain beside the name. Web search
 * sources are named by their domain (the provider's title is the publisher
 * domain), and "indeed.com  indeed.com" says the same thing twice.
 */
export function sourceShowsDomain(source: StreamingSource): boolean {
  return comparableHost(source.name) !== comparableHost(source.domain);
}

/** One or two letters for the neutral avatar disc: "Joy Cone" → "JC". */
export function sourceInitials(source: StreamingSource): string {
  const words = source.name.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return `${words[0]![0]}${words[1]![0]}`.toUpperCase();
  return source.name.slice(0, 1).toUpperCase() || source.domain.slice(0, 1).toUpperCase();
}

/**
 * The three muted tones a stack of source marks cycles through — the same
 * accent / orange / green the trace uses, so sources and steps read as one
 * family. Web resolves them to `--agent-tone-*`, native to `getAgentTones()`.
 */
export const SOURCE_TONES = ['accent', 'orange', 'green'] as const;
export type SourceTone = (typeof SOURCE_TONES)[number];
